import React, { useEffect, useState, useRef, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Header from "../common/Header";
import {
  PageWrapper,
  Content,
  OrderHeader,
  ProductCard,
  UploadZone,
  UploadButton,
  ProgressBar,
  Progress,
  PreviewList,
  PreviewItem,
  Thumb,
  VideoThumb,
} from "./styled";
import { fetchPickupOrder, getPickupSignedUrls } from "utils/api";
import { format } from "date-fns";

import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";

import { getMerchantConfig } from "features/config/configSlice";
import { Skeleton } from "@mui/material";

const sanitizeUploadFilename = (filename) => {
  const rawName = String(filename || "")
    .replace(/\\/g, "/")
    .split("/")
    .pop()
    .trim();
  const normalizedName = rawName
    .normalize("NFKD")
    .split("")
    .filter((char) => char.charCodeAt(0) <= 127)
    .join("");
  const safeName = normalizedName
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^[._-]+|[._-]+$/g, "");

  if (!safeName) {
    return `upload_${Date.now()}`;
  }

  const extensionIndex = safeName.lastIndexOf(".");
  const hasExtension = extensionIndex > 0;
  const stem = (hasExtension ? safeName.slice(0, extensionIndex) : safeName)
    .slice(0, 100)
    .replace(/^[._-]+|[._-]+$/g, "") || "upload";
  const extension = hasExtension
    ? safeName
        .slice(extensionIndex)
        .toLowerCase()
        .replace(/[^A-Za-z0-9.]/g, "")
        .slice(0, 16)
    : "";

  return `${stem}${extension}`;
};

function PickupImages() {
  const [order, setOrder] = useState(null);
  const [filesByItem, setFilesByItem] = useState({});
  const [previewsByItem, setPreviewsByItem] = useState({});
  const [progressByItem, setProgressByItem] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const previewsRef = useRef(previewsByItem);

  const merchantConfig = useSelector(getMerchantConfig);

  const [params] = useSearchParams();
  const reference_id = params.get("rid");
  const awb = params.get("awb");

  const getPickupItemKey = (item, index) =>
    String(
      item?.return_request_line_item_id ||
        item?.line_item_id ||
        item?.id ||
        [item?.product_id, item?.variant_id, item?.product_sku]
          .filter(Boolean)
          .join("-") ||
        index,
    );

  useEffect(() => {
    fetchPickupOrder(reference_id, awb).then((resp) => {
      setOrder(resp.data);
    });
  }, [params]);

  const handleSelectFiles = (itemId, event) => {
    const newFiles = Array.from(event.target.files);
    if (newFiles.length === 0) {
      return;
    }

    setFilesByItem((prev) => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), ...newFiles],
    }));
    setPreviewsByItem((prev) => ({
      ...prev,
      [itemId]: [
        ...(prev[itemId] || []),
        ...newFiles.map((f) => ({
          name: f.name,
          size: f.size,
          type: f.type,
          preview: URL.createObjectURL(f),
          url: "",
        })),
      ],
    }));
    // reset input so selecting the same file again triggers onChange
    event.target.value = "";
  };

  const handleRemoveFile = (itemId, fileIndex) => {
    setFilesByItem((prev) => ({
      ...prev,
      [itemId]: (prev[itemId] || []).filter((_, index) => index !== fileIndex),
    }));
    setPreviewsByItem((prev) => {
      const removedPreview = prev[itemId]?.[fileIndex]?.preview;
      if (removedPreview) {
        URL.revokeObjectURL(removedPreview);
      }

      return {
        ...prev,
        [itemId]: (prev[itemId] || []).filter(
          (_, index) => index !== fileIndex,
        ),
      };
    });
  };

  useEffect(() => {
    previewsRef.current = previewsByItem;
  }, [previewsByItem]);

  useEffect(() => {
    return () => {
      Object.values(previewsRef.current).forEach((arr) => {
        arr.forEach((f) => {
          if (f.preview) URL.revokeObjectURL(f.preview);
        });
      });
    };
  }, []);

  async function uploadToS3(file, postData, onProgress) {
    const formData = new FormData();
    Object.entries(postData.fields).forEach(([k, v]) => formData.append(k, v));
    formData.append("file", file);

    const response = await fetch(postData.url, {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Upload failed: ${response.status} ${text}`);
    }
    if (onProgress) onProgress();
    return true;
  }

  const handleUpload = async () => {
    if (!order || uploading) return;

    setUploading(true);

    const itemIds = Object.keys(filesByItem);
    const initialProgress = {};
    itemIds.forEach((id) => {
      if (filesByItem[id] && filesByItem[id].length > 0) {
        initialProgress[id] = 0;
      }
    });
    setProgressByItem((prev) => ({ ...prev, ...initialProgress }));

    for (const itemId of itemIds) {
      const fileList = filesByItem[itemId];
      if (!fileList || fileList.length === 0) continue;

      const signedUploads = fileList.map((file, index) => ({
        file,
        requestName: `${index}_${sanitizeUploadFilename(file.name)}`,
      }));

      const filesMapping = signedUploads.map(({ file, requestName }) => ({
        name: requestName,
        content_type: file.type,
      }));

      const resp = await getPickupSignedUrls(
        awb,
        filesMapping,
        order.order_id,
        order.order_number,
        order.id
      );

      // Use reducers here because Object.fromEntries is unavailable in some
      // older mobile browsers and embedded webviews.
      const postMap = resp.data.posts.reduce((acc, post) => {
        acc[post.name] = post.post_data;
        return acc;
      }, {});
      const urlMap = resp.data.posts.reduce((acc, post) => {
        acc[post.name] = post.file_url;
        return acc;
      }, {});

      let completed = 0;
      const total = fileList.length;

      await Promise.all(
        signedUploads.map(({ file, requestName }) => {
          const postData = postMap[requestName];
          if (!postData) {
            throw new Error(`No presigned URL for ${file.name}`);
          }
          return uploadToS3(file, postData, () => {
            completed += 1;
            setProgressByItem((prev) => ({
              ...prev,
              [itemId]: Math.round((completed / total) * 100),
            }));
          }).then(() => {
            const url = urlMap[requestName] || "";
            setPreviewsByItem((prev) => ({
              ...prev,
              [itemId]: prev[itemId].map((obj) =>
                obj.name === file.name ? { ...obj, url } : obj
              ),
            }));
          });
        })
      );

      setProgressByItem((prev) => ({ ...prev, [itemId]: 100 }));
      setTimeout(() => {
        setProgressByItem((prev) => {
          const updated = { ...prev };
          delete updated[itemId];
          return updated;
        });
      }, 1000);
    }

    setUploading(false);
    setUploadComplete(true);
  };

  return (
    <>
      <Header />
      <PageWrapper>
        <Content>
          {order ? (
            <Fragment>
              <OrderHeader>
                <div className="order-sub-container">
                  <CalendarTodayOutlinedIcon
                    style={{
                      color: merchantConfig.primary_theme_color,
                    }}
                    className="order-icons"
                  />
                  <div>
                    <h2 className="order-number">
                      Order #{order.order_number}
                    </h2>
                    {order?.created_at && (
                      <p className="created-at">
                        {format(new Date(order.created_at), "MMMM dd yyyy")} at{" "}
                        {format(new Date(order.created_at), "hh:mm aa")}
                      </p>
                    )}
                  </div>
                </div>
                <div className="order-sub-container">
                  <LocalShippingOutlinedIcon
                    className="order-icons"
                    style={{
                      color: "#5EA842",
                    }}
                  />
                  <div className="awb-container">
                    <p className="awb">AWB: {awb}</p>
                    <p>{order.line_items.length} items</p>
                  </div>
                </div>
              </OrderHeader>

              <h3>Order Items</h3>
              {order.line_items.map((item, index) => {
                const itemKey = getPickupItemKey(item, index);
                const itemPreviews = previewsByItem[itemKey] || [];
                const fileInputId = `file-${itemKey}`;
                const hasPreviews = itemPreviews.length > 0;

                return (
                  <ProductCard key={itemKey}>
                    <div className="product-detail-container">
                      <div className="product-image-container">
                        <img
                          src={item.variant_image_url}
                          alt={item.product_title}
                          className="product-image"
                        />
                      </div>
                      <div className="details-wrapper">
                        <h3 className="product-title">{item.product_title}</h3>
                        <div className="details">
                          {item.product_sku && (
                            <p>
                              <span className="side-heading">SKU: </span>
                              <span className="detail-value">
                                {item.product_sku}
                              </span>
                            </p>
                          )}
                          {item.variant_name && (
                            <p>
                              <span className="side-heading">Variant: </span>
                              <span className="detail-value">
                                {item.variant_name}
                              </span>
                            </p>
                          )}
                          {item.quantity && (
                            <p>
                              <span className="side-heading">Quantity: </span>
                              <span className="detail-value">
                                {item.quantity}
                              </span>
                            </p>
                          )}
                          {item.product_price && (
                            <p>
                              <span className="side-heading">Price: </span>
                              <span className="detail-value">
                                ₹{Number(item.product_price).toFixed(2)}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {!uploading && !uploadComplete && !hasPreviews ? (
                      <UploadZone
                        htmlFor={fileInputId}
                        primary_theme_color={merchantConfig.primary_theme_color}
                      >
                        <span className="upload-icon">
                          <FileUploadOutlinedIcon />
                        </span>
                        <span className="upload-title">Add pickup proof</span>
                        <span className="upload-subtitle">
                          Upload images or videos for this item
                        </span>
                      </UploadZone>
                    ) : null}

                    <input
                      id={fileInputId}
                      type="file"
                      multiple
                      accept="image/*,video/*"
                      style={{ display: "none" }}
                      onChange={(e) => handleSelectFiles(itemKey, e)}
                    />

                    {hasPreviews && (
                      <PreviewList>
                        {itemPreviews.map((f, previewIndex) => (
                          <PreviewItem key={`${f.name}-${previewIndex}`}>
                            {f.preview &&
                              (f.type.startsWith("image") ? (
                                <Thumb src={f.preview} alt={f.name} />
                              ) : (
                                <VideoThumb
                                  src={f.preview}
                                  preload="metadata"
                                  controls
                                />
                              ))}
                            {!uploading && !uploadComplete && (
                              <button
                                type="button"
                                className="preview-remove"
                                aria-label={`Remove ${f.name}`}
                                onClick={() =>
                                  handleRemoveFile(itemKey, previewIndex)
                                }
                              >
                                <CloseIcon />
                              </button>
                            )}
                            <div className="preview-meta">
                              <p>{f.name}</p>
                              <span>{(f.size / 1024).toFixed(2)} KB</span>
                            </div>
                          </PreviewItem>
                        ))}
                        {!uploading && !uploadComplete && (
                          <PreviewItem
                            as="label"
                            htmlFor={fileInputId}
                            className="preview-add-tile"
                          >
                            <FileUploadOutlinedIcon />
                            <span>Add more</span>
                          </PreviewItem>
                        )}
                      </PreviewList>
                    )}

                    {progressByItem[itemKey] !== undefined && (
                      <div style={{ marginTop: "8px" }}>
                        <ProgressBar>
                          <Progress
                            value={progressByItem[itemKey]}
                            primary_theme_color={
                              merchantConfig.primary_theme_color
                            }
                          />
                        </ProgressBar>
                        <p style={{ textAlign: "center", marginTop: "4px" }}>
                          {progressByItem[itemKey]}% uploaded
                        </p>
                      </div>
                    )}
                  </ProductCard>
                );
              })}
              {uploadComplete && (
                <div className="upload-complete-message">
                  <CheckCircleOutlinedIcon />
                  Upload has completed
                </div>
              )}

              {order && !uploadComplete && (
                <UploadButton
                  onClick={handleUpload}
                  disabled={uploading}
                  primary_theme_color={merchantConfig.primary_theme_color}
                  rounded={merchantConfig.use_rounded_edge_buttons}
                >
                  {uploading ? "Uploading..." : "Upload"}
                </UploadButton>
              )}
            </Fragment>
          ) : (
            <div style={{ padding: "100px" }}>
              <Skeleton style={{ width: "100%" }} height={50} />
              <Skeleton
                style={{ width: "100%", marginTop: "1rem" }}
                height={50}
              />
              <Skeleton
                style={{ width: "100%", marginTop: "1rem" }}
                height={50}
              />
              <Skeleton
                style={{ width: "100%", marginTop: "1rem" }}
                height={50}
              />
            </div>
          )}
        </Content>
      </PageWrapper>
    </>
  );
}

export default PickupImages;
